import {
  Box,
  Button,
  DialogProps,
  Grid,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import React, { Fragment, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { GridColDef } from '@mui/x-data-grid'
import dayjs from 'dayjs'
import { Link } from 'react-router-dom'
import { cnjMask } from 'utils/mask.util'
import client from 'services/client'
import { BaseData } from 'types/base.type'
import { CalendarIcon } from '@mui/x-date-pickers'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import CloseIcon from '@mui/icons-material/Close'
import DialogContent from '@mui/material/DialogContent'
import FormInputDatePicker from 'components/FormInputDatePicker'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import { DataGridCustom } from 'pages/CreateCreas/components/TableData/style'
import { FormInputAutoComplete } from 'components/FormInputAutoComplete'
import { usePromotoriaContext } from 'contexts/promotoria.context'

type AdolescenteAgendadoType = {
  id?: number | null
  processo?: {
    id?: number | null
    numeroCnj?: string | null
  }
  nomeAdolescente?: string | null
  dataAgendamento?: string | null
  indicadorIntegracao?: string | null
}

type AdolescenteAgendadoFiltroType = {
  promotoria?: number
  dataInicio?: string | null
  dataFim?: string | null
}

export const AdolescentesAgendadosModal = () => {
  const theme = useTheme()

  const { promotorias, promotoriaSelecionada } = usePromotoriaContext()

  const [fullWidth] = useState(true)
  const [maxWidth] = useState<DialogProps['maxWidth']>('lg')

  const [isFetching, setFetching] = useState<boolean>(true)
  const [open, setOpen] = useState<boolean>(false)
  const [data, setData] = useState<AdolescenteAgendadoType[]>([])

  const form = useForm<AdolescenteAgendadoFiltroType>()
  const { control, handleSubmit, reset, setValue, watch } = form

  const promotoriaWatch = watch('promotoria')

  const handleOpen = () => {
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
    handleReset()
  }

  const handleReset = () => {
    reset({
      promotoria: promotoriaSelecionada?.id,
      dataInicio: '',
      dataFim: '',
    })
  }

  const columns = useMemo<GridColDef<AdolescenteAgendadoType, any, any>[]>(
    () => [
      {
        field: 'dataAgendamento',
        headerName: 'Data de agendamento',
        flex: 1,
        sortable: false,
        disableReorder: true,
        disableExport: true,
        disableColumnMenu: true,
        renderCell: (params) => dayjs(params.value).format('DD/MM/YYYY'),
        valueGetter: (params) => params.value,
      },
      {
        field: 'processo',
        headerName: 'Número CNJ',
        flex: 1,
        sortable: false,
        disableReorder: true,
        disableExport: true,
        disableColumnMenu: true,
        renderCell: (params) => (
          <Link
            to={`/processos-pendentes/${params.row.processo?.id}`}
            style={{ color: theme.palette.text.primary, textDecoration: 'none' }}
            state={{
              promotoria: promotoriaWatch,
            }}
          >
            <Typography
              variant={'body2'}
              sx={{
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              {cnjMask(params.value)}
            </Typography>
          </Link>
        ),
        valueGetter: (params) => params.value.numeroCnj,
      },
      {
        field: 'nomeAdolescente',
        headerName: 'Adolescente',
        flex: 1,
        sortable: false,
        disableReorder: true,
        disableExport: true,
        disableColumnMenu: true,
        renderCell: (params) => params.value,
        valueGetter: (params) => params.value,
      },
    ],
    [promotoriaWatch],
  )

  const buscar = async (data?: AdolescenteAgendadoFiltroType) => {
    const url = '/integra-policial/processos'
    setFetching(true)
    await client
      .get<BaseData<AdolescenteAgendadoType[]>>(`${url}/adolescentes-agendados`, { ...data })
      .then((response) => {
        if (response && response.data) {
          setData(response.data)
        }
      })
      .finally(() => setFetching(false))
  }

  useEffect(() => {
    ;(async () => {
      if (open) {
        await handleSubmit(async (data) => {
          await buscar(data)
        })()
      }
    })()
  }, [open])

  useEffect(() => {
    setValue('promotoria', promotoriaSelecionada?.id)
  }, [promotoriaSelecionada])

  return (
    <Fragment>
      <Box display={'flex'} flexDirection={'row'} justifyContent={'end'}>
        <Tooltip title={'Oitivas informais agendadas.'}>
          <IconButton size={'large'} onClick={handleOpen}>
            <CalendarIcon fontSize={'large'} />
          </IconButton>
        </Tooltip>
      </Box>
      <Dialog open={open} onClose={handleClose} fullWidth={fullWidth} maxWidth={maxWidth}>
        <DialogTitle>
          <Typography variant={'h6'} color={'primary.main'}>
            Oitivas informais agendadas
          </Typography>
        </DialogTitle>
        <IconButton
          aria-label='close'
          onClick={handleClose}
          sx={(theme) => ({
            position: 'absolute',
            right: 8,
            top: 8,
            color: theme.palette.grey[500],
          })}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent>
          <Box display={'flex'} flexDirection={'column'} gap={2}>
            <form>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormInputAutoComplete
                    label={'Promotoria'}
                    name={'promotoria'}
                    control={control}
                    options={promotorias}
                    getOptionLabel={(option) => option.descricao}
                    getOptionById={(option: string, options) => {
                      return options.find((item) => item.id === Number(option)) || null
                    }}
                    isOptionEqualToValue={(option, value) => option.id === value?.id}
                    onChangeValue={(option) => String(option?.id) || null}
                  />
                </Grid>
                <Grid item md={4} xs={12}>
                  <FormInputDatePicker
                    label={'Data de início'}
                    name={'dataInicio'}
                    control={control}
                    enableFuture
                  />
                </Grid>
                <Grid item md={4} xs={12}>
                  <FormInputDatePicker
                    label={'Até'}
                    name={'dataFim'}
                    control={control}
                    enableFuture
                  />
                </Grid>
                <Grid item md={4} xs={12}>
                  <Box display={'flex'} gap={2}>
                    <Button variant='outlined' color='secondary' onClick={handleReset}>
                      Limpar
                    </Button>
                    <Button
                      variant='contained'
                      color='secondary'
                      startIcon={<SearchRoundedIcon />}
                      onClick={async () => {
                        await handleSubmit(async (data) => {
                          await buscar(data)
                        })()
                      }}
                    >
                      Pesquisar
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </form>
            <DataGridCustom
              loading={isFetching}
              rows={data}
              columns={columns}
              localeText={{
                noRowsLabel: 'Nenhum resultado encontrado.',
              }}
              autoHeight
              hideFooterPagination
              hideFooter
            />
            <Box display={'flex'} justifyContent={'end'}>
              <Button variant={'outlined'} color={'secondary'} onClick={handleClose}>
                Voltar
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </Fragment>
  )
}
